import { Check, Loader2, MessageSquarePlus, Upload, Video } from 'lucide-react'
import { useVideoUpload } from 'modules/common/hooks/use-video-upload'
import { apiCall } from 'modules/core/services/api'
import { CreativeService } from 'openapi/services/CreativeService'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useEditorModalClose } from '../../components/EditorModal'
import { useChatPrefillStore, useSectionContextStore } from '../../store/conversation-store'

interface GalleryVideo {
  attachmentId: string
  videoUrl: string
  createdAt: string
  mimeType?: string
  ratio?: number
}

interface VideoPickerBodyProps {
  creativeId: string
  /** Creative shape (e.g. facebook_post). Passed through to upload analytics. */
  creativeType?: string
  /** Per-platform editor that opened this picker. Passed through to upload analytics. */
  platform?: string
  /** Currently-selected video attachment ID, used to mark the active gallery tile. */
  selectedVideoAttachmentId?: string | null
  /**
   * Currently-selected video URL. Lets us render the active tile even when the
   * API gallery (which scans chat history) doesn't include it — e.g. the
   * original video, or a user-uploaded one that isn't in chat history.
   */
  selectedVideoUrl?: string | null
  /**
   * Called when the user picks a different video (gallery tile or fresh upload).
   * `source` records provenance: TikTok labels Taka-made video as AI-generated
   * and must NOT label the customer's own upload.
   */
  onSelectVideo: (attachmentId: string, videoUrl: string, source: 'generated' | 'uploaded') => void
}

/**
 * Edit Video modal body. Two horizontal rails:
 *   1. CREATED VIDEOS — AI generations (from chat history) + the
 *      currently-selected video if it's not in the API list (so the original
 *      is always visible). Ends with an "Ask AI" tile.
 *   2. UPLOADED VIDEOS — videos the user uploaded in this modal session. Ends
 *      with an "Upload" tile. Uploads do not persist across modal re-opens
 *      until the server tracks per-creative uploads.
 */
export function VideoPickerBody({
  creativeId,
  creativeType,
  platform,
  selectedVideoAttachmentId,
  selectedVideoUrl,
  onSelectVideo,
}: VideoPickerBodyProps) {
  const [createdVideos, setCreatedVideos] = useState<GalleryVideo[]>([])
  const [uploadedVideos, setUploadedVideos] = useState<GalleryVideo[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const setContext = useSectionContextStore(s => s.setContext)
  const setPrefill = useChatPrefillStore(s => s.setPrefill)
  const closeModal = useEditorModalClose()

  const loadVideos = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    apiCall(CreativeService.creativeControllerGetGeneratedVideosV1, null, creativeId)
      .then(data => {
        if (!cancelled) setCreatedVideos(data.videos || [])
      })
      .catch(err => {
        if (!cancelled) {
          setCreatedVideos([])
          setError(err instanceof Error ? err.message : 'Failed to load videos')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [creativeId])

  useEffect(() => loadVideos(), [loadVideos])

  // If the currently-selected video isn't in either list (typical for the
  // "original" video that pre-dates our chat-history scan window), inject it
  // at the head of CREATED so the user can still see and re-pick it.
  const displayCreated = useMemo<GalleryVideo[]>(() => {
    if (!selectedVideoUrl) return createdVideos
    const inCreated = createdVideos.some(
      v =>
        (selectedVideoAttachmentId && v.attachmentId === selectedVideoAttachmentId) ||
        v.videoUrl === selectedVideoUrl
    )
    const inUploaded = uploadedVideos.some(
      v =>
        (selectedVideoAttachmentId && v.attachmentId === selectedVideoAttachmentId) ||
        v.videoUrl === selectedVideoUrl
    )
    if (inCreated || inUploaded) return createdVideos
    return [
      {
        attachmentId: selectedVideoAttachmentId ?? '',
        videoUrl: selectedVideoUrl,
        createdAt: '',
      },
      ...createdVideos,
    ]
  }, [createdVideos, uploadedVideos, selectedVideoAttachmentId, selectedVideoUrl])

  const { handleUpload, isUploading, validationError, resetValidationError } = useVideoUpload({
    creativeId,
    creativeType,
    platform,
    fieldId: 'video',
  })

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      // Reset the input so picking the same file twice in a row still fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (!file) return
      try {
        const attachment = await handleUpload(file)
        setUploadedVideos(prev => [
          {
            attachmentId: attachment.id,
            videoUrl: attachment.publicUrl,
            createdAt: attachment.createdAt,
            mimeType: attachment.mimeType,
            ratio: attachment.ratio,
          },
          ...prev,
        ])
        onSelectVideo(attachment.id, attachment.publicUrl, 'uploaded')
      } catch (err) {
        // useVideoUpload sets validationError for the typed cases; surface
        // anything else (network etc.) via the console bridge.
        console.error('VideoPickerBody: upload failed:', err)
      }
    },
    [handleUpload, onSelectVideo]
  )

  const handleAskAIRegenerate = useCallback(() => {
    setContext(creativeId, {
      sectionId: 'video',
      sectionType: 'video',
      label: 'Video',
      preview: 'Regenerate the video',
    })
    setPrefill(creativeId, 'Regenerate the video with ')
    closeModal?.()
  }, [creativeId, setContext, setPrefill, closeModal])

  const isSelected = useCallback(
    (v: GalleryVideo) =>
      (!!selectedVideoAttachmentId && v.attachmentId === selectedVideoAttachmentId) ||
      (!selectedVideoAttachmentId && !!selectedVideoUrl && v.videoUrl === selectedVideoUrl),
    [selectedVideoAttachmentId, selectedVideoUrl]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-paper-faint" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <Video size={32} className="text-paper-faint" />
        <p className="text-[13px] text-paper-dim">{error}</p>
        <button
          type="button"
          onClick={loadVideos}
          className="mt-1 text-[13px] font-medium text-acid hover:underline"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <VideoRow label="Created videos">
        {displayCreated.map(v => (
          <VideoTile
            key={v.attachmentId || v.videoUrl}
            video={v}
            selected={isSelected(v)}
            onClick={() => onSelectVideo(v.attachmentId, v.videoUrl, 'generated')}
          />
        ))}
        <CtaTile
          icon={<MessageSquarePlus size={20} />}
          label="Ask AI to create"
          onClick={handleAskAIRegenerate}
        />
      </VideoRow>

      <VideoRow label="Uploaded videos">
        {uploadedVideos.map(v => (
          <VideoTile
            key={v.attachmentId}
            video={v}
            selected={isSelected(v)}
            onClick={() => onSelectVideo(v.attachmentId, v.videoUrl, 'uploaded')}
          />
        ))}
        <CtaTile
          icon={isUploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
          label={isUploading ? 'Uploading…' : 'Upload video'}
          onClick={() => {
            resetValidationError()
            fileInputRef.current?.click()
          }}
          disabled={isUploading}
        />
      </VideoRow>

      {validationError && (
        <p className="text-[12px] text-[color:var(--alert-error-text)]">
          {validationError.message}
        </p>
      )}
      <p className="text-[11px] text-paper-faint">
        MP4 only · vertical 9:16 · 30 seconds max · 50 MB max.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  )
}

function VideoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">{label}</p>
      <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-1">
        {children}
      </div>
    </div>
  )
}

interface VideoTileProps {
  video: GalleryVideo
  selected: boolean
  onClick: () => void
}

function VideoTile({ video, selected, onClick }: VideoTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative aspect-[9/16] w-[132px] flex-none snap-start overflow-hidden rounded-[8px] border-2 bg-black transition-all ${
        selected
          ? 'border-acid shadow-[0_0_0_3px_var(--acid-soft)]'
          : 'border-line hover:border-line-bright'
      }`}
    >
      <video
        src={video.videoUrl}
        className="h-full w-full object-cover"
        preload="metadata"
        muted
        playsInline
      />
      {selected && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <div className="rounded-full bg-acid p-1.5">
            <Check size={14} className="text-ink-on-acid" />
          </div>
        </div>
      )}
    </button>
  )
}

interface CtaTileProps {
  icon: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
}

function CtaTile({ icon, label, onClick, disabled }: CtaTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group relative flex aspect-[9/16] w-[132px] flex-none snap-start flex-col items-center justify-center gap-2 rounded-[8px] border-2 border-dashed border-line bg-bg-2 text-[12px] font-medium text-paper-faint transition-colors hover:border-acid hover:bg-[color:var(--acid-tint)] hover:text-acid disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-line disabled:hover:bg-bg-2 disabled:hover:text-paper-faint"
    >
      {icon}
      <span className="px-2 text-center leading-tight">{label}</span>
    </button>
  )
}
